import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { registerDestructor } from "@ember/destroyable";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import { ajax } from "discourse/lib/ajax";
import { eq } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";

const ROTATION_INTERVAL = 6000;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export default class ModMarketBanner extends Component {
  @tracked banners = [];
  @tracked activeIndex = 0;
  @tracked autoplayEnabled = true;
  @tracked isLoading = true;

  #destroyed = false;
  #focusPaused = false;
  #hoverPaused = false;
  #timer;
  #motionPreference;

  constructor() {
    super(...arguments);

    this.#motionPreference = window.matchMedia(REDUCED_MOTION_QUERY);
    this.autoplayEnabled = !this.#motionPreference.matches;
    this.#motionPreference.addEventListener(
      "change",
      this.updateMotionPreference
    );
    document.addEventListener("visibilitychange", this.updateVisibility);

    registerDestructor(this, () => {
      this.#destroyed = true;
      this.#clearTimer();
      this.#motionPreference.removeEventListener(
        "change",
        this.updateMotionPreference
      );
      document.removeEventListener("visibilitychange", this.updateVisibility);
    });

    this.loadBanners(this.args.context || "mods");
  }

  get currentBanner() {
    return this.banners[this.activeIndex];
  }

  get currentPosition() {
    return this.activeIndex + 1;
  }

  get canRotate() {
    return this.banners.length > 1;
  }

  get usesPeekLayout() {
    return this.args.layout === "peek" && this.canRotate;
  }

  get peekSlides() {
    if (!this.usesPeekLayout) {
      return [];
    }

    const previousIndex =
      (this.activeIndex - 1 + this.banners.length) % this.banners.length;
    const nextIndex = (this.activeIndex + 1) % this.banners.length;

    return [
      {
        banner: this.banners[previousIndex],
        position: "previous",
      },
      { banner: this.currentBanner, position: "current" },
      { banner: this.banners[nextIndex], position: "next" },
    ];
  }

  async loadBanners(context) {
    this.isLoading = true;

    try {
      const response = await ajax("/mod-market-api/v1/banners.json", {
        data: { context },
      });
      if (this.#destroyed || !Array.isArray(response.banners)) {
        return;
      }

      const banners =
        response.mode === "single"
          ? response.banners.slice(0, 1)
          : response.banners;
      this.banners = banners.map((banner, index) => ({
        ...banner,
        position: index + 1,
      }));
      this.activeIndex = 0;
      this.#resetTimer();
    } catch {
      if (!this.#destroyed) {
        this.banners = [];
        this.#clearTimer();
      }
    } finally {
      if (!this.#destroyed) {
        this.isLoading = false;
      }
    }
  }

  @action
  contextChanged() {
    this.banners = [];
    this.activeIndex = 0;
    this.#clearTimer();
    this.loadBanners(this.args.context || "mods");
  }

  @action
  showPrevious() {
    this.#showBanner(
      (this.activeIndex - 1 + this.banners.length) % this.banners.length
    );
  }

  @action
  showNext() {
    this.#showBanner((this.activeIndex + 1) % this.banners.length);
  }

  @action
  showBanner(index) {
    this.#showBanner(index);
  }

  @action
  toggleAutoplay() {
    this.autoplayEnabled = !this.autoplayEnabled;
    this.#resetTimer();
  }

  @action
  pauseForHover() {
    this.#hoverPaused = true;
    this.#clearTimer();
  }

  @action
  resumeAfterHover() {
    this.#hoverPaused = false;
    this.#resetTimer();
  }

  @action
  pauseForFocus() {
    this.#focusPaused = true;
    this.#clearTimer();
  }

  @action
  resumeAfterFocus(event) {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    this.#focusPaused = false;
    this.#resetTimer();
  }

  @action
  updateMotionPreference(event) {
    this.autoplayEnabled = !event.matches;
    this.#resetTimer();
  }

  @action
  updateVisibility() {
    this.#resetTimer();
  }

  #showBanner(index) {
    this.activeIndex = index;
    this.#resetTimer();
  }

  #resetTimer() {
    this.#clearTimer();
    if (
      !this.canRotate ||
      !this.autoplayEnabled ||
      this.#hoverPaused ||
      this.#focusPaused ||
      document.hidden
    ) {
      return;
    }

    this.#timer = window.setTimeout(() => {
      this.activeIndex = (this.activeIndex + 1) % this.banners.length;
      this.#resetTimer();
    }, ROTATION_INTERVAL);
  }

  #clearTimer() {
    if (this.#timer) {
      window.clearTimeout(this.#timer);
      this.#timer = undefined;
    }
  }

  <template>
    <div
      class={{dConcatClass
        "mod-market-banner-loader"
        (if this.isLoading "is-loading")
      }}
      {{didUpdate this.contextChanged @context}}
      ...attributes
    >
      {{#if this.currentBanner}}
        <section
          class={{dConcatClass
            "mod-market-banner"
            (if this.usesPeekLayout "--peek")
          }}
          aria-label={{i18n "discourse_mod_market.banner.label"}}
          aria-roledescription={{i18n "discourse_mod_market.banner.carousel"}}
          {{on "mouseenter" this.pauseForHover}}
          {{on "mouseleave" this.resumeAfterHover}}
          {{on "focusin" this.pauseForFocus}}
          {{on "focusout" this.resumeAfterFocus}}
        >
          {{#if this.usesPeekLayout}}
            <div class="mod-market-banner__peek-track">
              {{#each this.peekSlides as |slide|}}
                <article
                  class={{dConcatClass
                    "mod-market-banner__slide"
                    "--peek"
                    (if (eq slide.position "previous") "--previous")
                    (if (eq slide.position "current") "--current")
                    (if (eq slide.position "next") "--next")
                    (unless slide.banner.cover_url "has-no-cover")
                  }}
                  aria-hidden={{unless (eq slide.position "current") "true"}}
                  aria-label={{if
                    (eq slide.position "current")
                    (i18n
                      "discourse_mod_market.banner.slide"
                      current=this.currentPosition
                      total=this.banners.length
                    )
                  }}
                  aria-roledescription={{if
                    (eq slide.position "current")
                    (i18n "discourse_mod_market.banner.slide_label")
                  }}
                >
                  {{#if slide.banner.cover_url}}
                    <img
                      class="mod-market-banner__image"
                      src={{slide.banner.cover_url}}
                      alt=""
                    />
                  {{/if}}
                  {{#if (eq slide.position "current")}}
                    {{#if slide.banner.target_url}}
                      <a
                        class="mod-market-banner__link"
                        href={{slide.banner.target_url}}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span class="sr-only">{{slide.banner.title}}</span>
                      </a>
                    {{/if}}
                    <div class="mod-market-banner__content">
                      <h2
                        class="mod-market-banner__title"
                      >{{slide.banner.title}}</h2>
                    </div>
                  {{/if}}
                </article>
              {{/each}}
            </div>
          {{else}}
            <article
              class={{dConcatClass
                "mod-market-banner__slide"
                (unless this.currentBanner.cover_url "has-no-cover")
              }}
              aria-label={{i18n
                "discourse_mod_market.banner.slide"
                current=this.currentPosition
                total=this.banners.length
              }}
              aria-roledescription={{i18n
                "discourse_mod_market.banner.slide_label"
              }}
            >
              {{#if this.currentBanner.cover_url}}
                <img
                  class="mod-market-banner__image"
                  src={{this.currentBanner.cover_url}}
                  alt=""
                />
              {{/if}}
              {{#if this.currentBanner.target_url}}
                <a
                  class="mod-market-banner__link"
                  href={{this.currentBanner.target_url}}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span class="sr-only">{{this.currentBanner.title}}</span>
                </a>
              {{/if}}
              <div class="mod-market-banner__content">
                <h2
                  class="mod-market-banner__title"
                >{{this.currentBanner.title}}</h2>
                {{#if this.currentBanner.game_label}}
                  <span
                    class="mod-market-banner__game"
                  >{{this.currentBanner.game_label}}</span>
                {{/if}}
                {{#if this.currentBanner.target_label}}
                  <span
                    class="mod-market-banner__target"
                  >{{this.currentBanner.target_label}}</span>
                {{/if}}
              </div>
            </article>
          {{/if}}

          {{#if this.canRotate}}
            <DButton
              @action={{this.showPrevious}}
              @icon="chevron-left"
              @translatedAriaLabel={{i18n
                "discourse_mod_market.banner.previous"
              }}
              class="btn-flat mod-market-banner__previous"
            />
            <DButton
              @action={{this.showNext}}
              @icon="chevron-right"
              @translatedAriaLabel={{i18n "discourse_mod_market.banner.next"}}
              class="btn-flat mod-market-banner__next"
            />
            <div class="mod-market-banner__controls">
              <div class="mod-market-banner__pagination">
                {{#each this.banners as |banner index|}}
                  <button
                    type="button"
                    class={{dConcatClass
                      "mod-market-banner__dot"
                      (if (eq this.activeIndex index) "is-active")
                    }}
                    aria-current={{if
                      (eq this.activeIndex index)
                      "true"
                      "false"
                    }}
                    aria-label={{i18n
                      "discourse_mod_market.banner.select"
                      number=banner.position
                      title=banner.title
                    }}
                    {{on "click" (fn this.showBanner index)}}
                  >
                    <span
                      class="mod-market-banner__dot-indicator"
                      aria-hidden="true"
                    ></span>
                  </button>
                {{/each}}
              </div>
              <DButton
                @action={{this.toggleAutoplay}}
                @icon={{if this.autoplayEnabled "pause" "play"}}
                @translatedAriaLabel={{if
                  this.autoplayEnabled
                  (i18n "discourse_mod_market.banner.pause")
                  (i18n "discourse_mod_market.banner.play")
                }}
                class="btn-flat mod-market-banner__autoplay"
              />
            </div>
          {{/if}}
        </section>
      {{/if}}
    </div>
  </template>
}
